import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as Popover from '@radix-ui/react-popover';
import { Search, X, RefreshCw } from 'lucide-react';
import { api, type ListedUser } from '@/lib/api';
import { useUsersStore } from '@/store/users-store';
import { cn } from '@/lib/utils';

// =============================================================================
// UserPicker — searchable combobox untuk pilih user (pemilik vehicle).
// Pattern: type query → klik icon search → fetch backend /api/v1/user/list,
// hasil tampil di dropdown. Filter further di client-side (substring match
// ke real_name/login/id). Backend sudah scope by JWT level_id.
//
// Props:
//   value     = id user terpilih (atau 0 = belum pilih)
//   onChange  = callback (id) saat user pilih row
//   onChangeMeta = optional, kasih row lengkap (mis. untuk display).
// =============================================================================

interface Props {
  value: number;
  onChange: (id: number) => void;
  onChangeMeta?: (user: ListedUser | null) => void;
  placeholder?: string;
  /** Class tambahan untuk trigger box — mis. "search-cyan" (globals.css)
      untuk varian gradient biru→cyan di toolbar monitoring. */
  className?: string;
}

export function UserPicker({ value, onChange, onChangeMeta, placeholder = 'Cari user...', className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<ListedUser | null>(null);

  // Cache rows tersimpan di users-store (session/localStorage). Initial buka
  // dialog langsung pakai cache; tombol refresh memaksa re-fetch.
  const cachedRows = useUsersStore((s) => s.rows);
  const cachedLoadedAt = useUsersStore((s) => s.loadedAt);
  const setCacheRows = useUsersStore((s) => s.setRows);

  const fetcher = useMutation({
    mutationFn: async () => {
      const res = await api.userList();
      // Guard: respons error/bentuk lama bisa tanpa rows array — jangan
      // meracuni cache (persisted) dengan non-array → crash .map di render.
      setCacheRows(Array.isArray(res.rows) ? res.rows : []);
      return res;
    },
  });

  // Sumber rows: hasil fetch baru (kalau ada) > cache. Filter di client.
  // Guard Array.isArray: localStorage webgps-users-cache bisa berisi bentuk
  // lama (origin localhost:5173 dipakai beberapa app dev).
  const sourceRows = Array.isArray(fetcher.data?.rows)
    ? fetcher.data.rows
    : Array.isArray(cachedRows) ? cachedRows : [];
  const filtered = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return sourceRows;
    return sourceRows.filter((u) =>
      `${u.real_name} ${u.login} ${u.id} ${u.email ?? ''}`.toLowerCase().includes(q),
    );
  })();

  const handleSearch = () => {
    fetcher.mutate();
  };

  const handlePick = (u: ListedUser) => {
    setPicked(u);
    onChange(u.id);
    onChangeMeta?.(u);
    setOpen(false);
  };

  const handleClear = () => {
    setPicked(null);
    onChange(0);
    onChangeMeta?.(null);
  };

  // Label tampilan: picked > resolve dari cache (supaya tampil NAMA bukan #id
  // saat value sudah ke-set dari sesi lain) > fallback "#value".
  const resolved = picked ?? (value > 0 ? cachedRows.find((u) => u.id === value) ?? null : null);
  const displayLabel =
    resolved ? `${resolved.real_name || resolved.login} · #${resolved.id}`
    : value > 0 ? `#${value}`
    : '';
  const selectedSearchQuery = resolved ? (resolved.real_name || resolved.login || String(resolved.id)) : '';

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setQuery(selectedSearchQuery);
    if (nextOpen && sourceRows.length === 0 && fetcher.isIdle) fetcher.mutate();
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Popover.Trigger asChild>
        {/* Fill di wrapper (bukan input) supaya varian className bisa kasih
            gradient tanpa ketutup background solid input. */}
        <div className={cn(
          'flex items-stretch gap-0 rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring',
          className,
        )}>
          <input
            type="text"
            readOnly
            placeholder="(belum dipilih)"
            value={displayLabel}
            className="flex-1 px-2.5 py-1.5 text-sm bg-transparent outline-none cursor-pointer"
            onClick={() => handleOpenChange(true)}
          />
          {value > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
              aria-label="Clear"
              className="px-2 hover:bg-accent text-muted-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleOpenChange(true); handleSearch(); }}
            aria-label="Search"
            className="px-2.5 border-l border-input bg-muted/50 hover:bg-accent text-muted-foreground"
          >
            <Search className="w-3.5 h-3.5" />
          </button>
        </div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="w-[var(--radix-popover-trigger-width)] z-50 bg-popover border border-border rounded-md shadow-lg overflow-hidden"
        >
          <div className="flex min-w-0 gap-2 border-b border-border p-2">
            <input
              type="search"
              autoFocus
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              className="min-w-0 flex-1 px-2 py-1 text-sm rounded-md border border-input bg-background"
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={fetcher.isPending}
              title="Query backend"
              className="px-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              <Search className="w-3.5 h-3.5" />
            </button>
            {(fetcher.isSuccess || sourceRows.length > 0) && (
              <button
                type="button"
                onClick={handleSearch}
                title="Refresh dari backend"
                className="px-2 rounded-md hover:bg-accent text-muted-foreground"
              >
                <RefreshCw className={cn('w-3.5 h-3.5', fetcher.isPending && 'animate-spin')} />
              </button>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto">
            {fetcher.isIdle && sourceRows.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground text-center">
                Klik <Search className="w-3 h-3 inline mx-0.5" /> untuk load daftar user.
              </div>
            )}
            {fetcher.isIdle && sourceRows.length > 0 && cachedLoadedAt && (
              <div className="px-3 py-1.5 text-[10px] text-muted-foreground bg-muted/30 border-b border-border">
                Cached · {new Date(cachedLoadedAt).toLocaleString()} ·{' '}
                <button type="button" onClick={handleSearch} className="underline hover:text-foreground">refresh</button>
              </div>
            )}
            {fetcher.isPending && (
              <div className="p-3 text-xs text-muted-foreground text-center">Memuat…</div>
            )}
            {fetcher.isError && (
              <div className="p-3 text-xs text-destructive text-center">
                {fetcher.error instanceof Error ? fetcher.error.message : 'Gagal load'}
              </div>
            )}
            {!fetcher.isPending && filtered.length === 0 && sourceRows.length > 0 && (
              <div className="p-3 text-xs text-muted-foreground text-center">Tidak ada cocok</div>
            )}
            <ul className="divide-y divide-border">
              {filtered.map((u) => (
                <li
                  key={u.id}
                  onClick={() => handlePick(u)}
                  className={cn(
                    'px-3 py-2 cursor-pointer text-sm hover:bg-accent',
                    value === u.id && 'bg-accent font-medium',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{u.real_name || u.login}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">#{u.id}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {u.login}
                    {u.level && <span className="ml-2">• {u.level}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
